/**
 * Custom tiptap-markdown serialize/parse rules for mention nodes.
 *
 * Serializes mention nodes as markdown links:
 *   userMention  → [Label](user:refId)
 *   refMention   → [Label](ticket:refId)  /  [Label](report:refId)  /  [Label](vehicle:refId)
 *
 * On parse, markdown-it turns these into <a href="user:refId">Label</a>.
 * The updateDOM hook converts matching <a> tags into <span data-type="..."> elements
 * so that TipTap's parseDOM picks them up as mention nodes.
 */

import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { MentionType } from "./mention-type.enum";

/** refType prefixes that are recognised as mention links */
const MENTION_PREFIXES = [
  BackendResourceEnum.USER,
  BackendResourceEnum.TICKET,
  BackendResourceEnum.REPORT,
  BackendResourceEnum.VEHICLE
] as const;

type MentionPrefix = (typeof MENTION_PREFIXES)[number];

/**
 * Regex that matches href values like "user:507f1f77bcf86cd799439011"
 */
const MENTION_HREF_RE = new RegExp(`^(${MENTION_PREFIXES.join("|")}):(.+)$`);

/* ---------- helpers -------------------------------------------- */

function parseMentionHref(href: string): { prefix: MentionPrefix; id: string } | null {
  const m = MENTION_HREF_RE.exec(href);
  if (!m) return null;
  return { prefix: m[1] as MentionPrefix, id: m[2] };
}

function dataTypeForPrefix(prefix: MentionPrefix): string {
  return prefix === BackendResourceEnum.USER ? MentionType.USER : MentionType.REF;
}

/* ---------- serialize ------------------------------------------ */

/**
 * Create a tiptap-markdown serialize spec for a mention node.
 * Usage: add to `addStorage()` on the TipTap extension.
 */
export function createMentionSerializeSpec(_triggerChar: string, getRefType?: (node: any) => string) {
  return {
    serialize(state: any, node: any) {
      const label: string = node.attrs.label ?? node.attrs.id;
      const refType: string = getRefType ? getRefType(node) : BackendResourceEnum.USER;
      const id: string = node.attrs.id;
      // Write as markdown link without trigger char prefix.
      // The trigger char (@/#) is added by renderHTML when displaying the node,
      // so including it here would cause duplication on roundtrip.
      state.write(`[${label}](${refType}:${id})`);
    }
  };
}

/* ---------- parse (updateDOM hook) ----------------------------- */

/**
 * tiptap-markdown parse spec shared by both mention extensions.
 * Converts <a href="user:id">Label</a> into <span data-type="userMention" ...>.
 *
 * Should be added to ONE extension only (or guarded to run once) since it
 * operates on all matching <a> elements in the DOM.
 */
export function createMentionParseSpec() {
  return {
    updateDOM(element: HTMLElement) {
      const anchors = element.querySelectorAll("a");
      anchors.forEach((a) => {
        const href = a.getAttribute("href") ?? "";
        const parsed = parseMentionHref(href);
        if (!parsed) return;

        const span = element.ownerDocument.createElement("span");
        span.setAttribute("data-type", dataTypeForPrefix(parsed.prefix));
        span.setAttribute("data-id", parsed.id);

        const label = a.textContent ?? "";
        span.setAttribute("data-label", label);

        if (parsed.prefix !== BackendResourceEnum.USER) {
          span.setAttribute("data-ref-type", parsed.prefix);
        }

        span.textContent = label;
        a.replaceWith(span);
      });
    }
  };
}
